import { TeamMemberModel } from "generated/core/coreSchemas";
import useGetTeamDetails from "hooks/querys/teams/useGetTeamDetails";
import useGetUsersProfileBatch from "hooks/querys/users/useGetUsersProfileBatch";
import { FC, useEffect, useMemo, useState } from "react";
import { useHistory, Link, useParams, useLocation } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import Hero from "components/layouts/Hero/Hero";
import Article from "components/layouts/Article";
import DataTable from "components/ui/DataTable/DataTable";
import type {
  TAreaDataTable,
  TAreaDataTableAction,
  TAreaDataTableColumns,
  TTeamDetailDataTable,
  TTeamsDetailDataTableColumns
} from "./types";
import { FormattedMessage, useIntl } from "react-intl";
import PlusIcon from "assets/images/icons/PlusWhite.svg";
import Loader from "components/ui/Loader";
import EditTeam from "./actions/EditTeam";
import AddTeamMember from "./actions/AddTeamMember";
import EditTeamMember from "./actions/EditTeamMember";
import DeleteTeam from "./actions/DeleteTeam";
import RemoveTeamMember from "./actions/RemoveTeamMember";
import { TTeamsDetailDataTableAction } from "./types";
import { sortByString } from "helpers/table";
import RemoveAreaFromTeam from "pages/area-view/actions/RemoveAreaFromTeam";
import useGetAreas from "hooks/querys/areas/useGetAreas";
import { AreaResponse } from "generated/core/coreResponses";

export type TParams = {
  teamId: string;
};

export interface IProps {
  isAddingTeamMember?: boolean;
  isEditingTeamMember?: boolean;
  isRemovingTeamMember?: boolean;
  isEditingTeam?: boolean;
  isDeletingTeam?: boolean;
  isDeletingTeamArea?: boolean;
}

const columnOrder: TTeamsDetailDataTableColumns[] = [
  { key: "name", name: "teams.details.table.header.name", sortCompareFn: sortByString },
  { key: "email", name: "teams.details.table.header.user", sortCompareFn: sortByString },
  {
    key: "gender",
    name: "teams.details.table.header.gender",
    sortCompareFn: sortByString
  }
];

const columnOrderWithStatus: TTeamsDetailDataTableColumns[] = [
  ...columnOrder,
  { key: "status", name: "teams.details.table.header.status", sortCompareFn: sortByString }
];

const areaColumnOrder: TAreaDataTableColumns[] = [
  {
    key: "name",
    name: "teams.details.table.header.name",
    sortCompareFn: sortByString,
    rowHref: row => `/areas/${row.id}`
  },
  { key: "templates", name: "teams.details.table.header.templates", sortCompareFn: sortByString }
];

const TeamDetail: FC<IProps> = props => {
  const {
    isAddingTeamMember = false,
    isEditingTeamMember = false,
    isRemovingTeamMember = false,
    isEditingTeam = false,
    isDeletingTeam = false,
    isDeletingTeamArea = false
  } = props;

  const location = useLocation();
  const { teamId } = useParams<{ teamId: string }>();
  const history = useHistory();
  const intl = useIntl();

  const [managerSearchTerm, setManagerSearchTerm] = useState("");
  const [managerStatusFilter, setManagerStatusFilter] = useState<"all" | "confirmed" | "invited">("all");
  const [managerGenderFilter, setManagerGenderFilter] = useState<"all" | "male" | "female" | "other">("all");

  const [monitorSearchTerm, setMonitorSearchTerm] = useState("");
  const [monitorStatusFilter, setMonitorStatusFilter] = useState<"all" | "confirmed" | "invited">("all");
  const [monitorGenderFilter, setMonitorGenderFilter] = useState<"all" | "male" | "female" | "other">("all");

  const {
    data: { areasByTeam },
    isFetching: isFetchingAreas
  } = useGetAreas();

  /* Queries */
  const {
    data: team,
    managers,
    monitors,
    userIsAdmin,
    userIsManager,
    isLoading: isTeamLoading
  } = useGetTeamDetails(teamId);

  // Extract all unique member userIds for batch profile lookup
  const allMemberUserIds = useMemo(() => {
    const ids: (string | undefined)[] = [];
    managers.forEach(m => {
      if (m.userId) ids.push(m.userId);
      // @ts-ignore
      if (m._id) ids.push(m._id);
      // @ts-ignore
      if (m.id) ids.push(m.id);
    });
    monitors.forEach(m => {
      if (m.userId) ids.push(m.userId);
      // @ts-ignore
      if (m._id) ids.push(m._id);
      // @ts-ignore
      if (m.id) ids.push(m.id);
    });
    return ids;
  }, [managers, monitors]);

  const { getProfile, isLoading: isProfilesLoading } = useGetUsersProfileBatch(allMemberUserIds);

  const teamAreas = useMemo(() => {
    if (!areasByTeam) {
      return [];
    }

    // @ts-ignore incorrect schema
    const found = areasByTeam.find(teamArea => teamArea.team?.id === teamId);

    return found?.areas ? found.areas : [];
  }, [areasByTeam, teamId]);

  // If the team wasn't found, then redirect to the teams summary page.
  useEffect(() => {
    if (!isTeamLoading && !team) {
      toastr.warning(intl.formatMessage({ id: "errors.team.detail" }), "");
      history.push("/teams");
    }
  }, [history, intl, isTeamLoading, team]);

  /**
   * Map each member from the API to translated data table rows
   * @param members members from the API
   */
  const mapMembersToRows = useMemo(
    () => (members: TeamMemberModel[]) =>
      members.map<TTeamDetailDataTable>(member => {
        let statusSuffix: typeof member.status | "administrator" | "left" = member.status;
        if (member.role === "administrator") {
          statusSuffix = "administrator";
        } else if (member.role === "left") {
          statusSuffix = "left";
        }

        const profile = getProfile(member.userId, member.email);
        // Debug logging for profile lookup
        if (process.env.NODE_ENV !== "production") {
          console.log("[TeamDetail] member mapped:", {
            email: member.email,
            userId: member.userId,
            profile
          });
        }
        const profileFirstName = profile?.profileData?.firstName || "";
        const profileLastName = profile?.profileData?.lastName || "";
        const profileFullName = `${profileFirstName} ${profileLastName}`.trim();

        const rawGender = (profile?.gender || profile?.profileData?.gender || "").toLowerCase().trim();
        let genderDisplay = intl.formatMessage({ id: "teams.details.gender.notSpecified" });
        if (rawGender === "male") {
          genderDisplay = intl.formatMessage({ id: "teams.details.gender.male" });
        } else if (rawGender === "female") {
          genderDisplay = intl.formatMessage({ id: "teams.details.gender.female" });
        } else if (rawGender === "other") {
          genderDisplay = intl.formatMessage({ id: "teams.details.gender.other" });
        } else if (rawGender) {
          genderDisplay =
            profile?.gender || profile?.profileData?.gender || intl.formatMessage({ id: "teams.details.gender.other" });
        }

        return {
          // @ts-ignore `_id` not typed checked
          id: member._id,
          name: profileFullName || member.name || member.userId || "",
          email: member.email,
          gender: genderDisplay,
          genderRaw: rawGender || "not_specified",
          country: profile?.profileData?.country || "",
          status: intl.formatMessage({ id: `teams.details.table.status.${statusSuffix}` }),
          statusSuffix,
          userId: member.userId
        };
      }),
    [getProfile, intl]
  );

  /**
   * Map each area from the API to translated data table rows
   * @param areas members from the API
   */
  const mapAreasToRows = useMemo(
    () => (areas: AreaResponse[]) =>
      areas.map(area => {
        return {
          id: area?.data?.id || "",
          name: area?.data?.attributes?.name || "",
          templates:
            area?.data?.attributes?.reportTemplate
              ?.filter((t: any) => {
                if (!t) return false;
                if (t.public || t.default) return false;
                const lang = t.defaultLanguage || "en";
                const name = (t.name?.[lang] || t.name || "").toString().toLowerCase().trim();
                return (
                  name !== "default template" &&
                  name !== "default investigation" &&
                  name !== "forest watcher questionnaire" &&
                  !name.startsWith("default ")
                );
              })
              //@ts-ignore
              ?.map(template => template.name[template.defaultLanguage] || "")
              .filter(Boolean)
              .join(", ") || ""
        };
      }),
    []
  );

  const makeManager: TTeamsDetailDataTableAction = {
    name: "teams.details.table.actions.manager",
    value: "makeManager",
    href: memberRow => `${location.pathname}/edit/${memberRow.id}/manager`,
    shouldShow: memberRow => {
      return memberRow.statusSuffix === "confirmed";
    }
  };

  const makeMonitor: TTeamsDetailDataTableAction = {
    name: "teams.details.table.actions.monitor",
    value: "makeMonitor",
    href: memberRow => `${location.pathname}/edit/${memberRow.id}/monitor`
  };

  const makeAdmin: TTeamsDetailDataTableAction = {
    name: "teams.details.table.actions.admin",
    value: "makeAdmin",
    href: memberRow => `${location.pathname}/edit/${memberRow.userId}/admin`,
    shouldShow: memberRow => {
      return userIsAdmin && memberRow.statusSuffix === "confirmed";
    }
  };

  const removeMember: TTeamsDetailDataTableAction = {
    name: "teams.details.table.actions.remove",
    value: "removeFromTeam",
    href: memberRow => `${location.pathname}/remove/${memberRow.id}`
  };

  const investigateArea: TAreaDataTableAction = {
    name: "investigation.start",
    value: "investigate",
    href: areaRow => `/reporting/investigation/${areaRow.id}?scrollToTeamId=${teamId}`
  };

  const removeArea: TAreaDataTableAction = {
    name: "areas.details.teams.removeArea.title",
    value: "remove",
    href: memberRow => `${location.pathname}/removeArea/${memberRow.id}`
  };

  // Gender counts for Managers
  const managerGenderCounts = useMemo(() => {
    const rows = mapMembersToRows(managers);
    return {
      all: rows.length,
      male: rows.filter(m => m.genderRaw === "male").length,
      female: rows.filter(m => m.genderRaw === "female").length,
      other: rows.filter(m => m.genderRaw !== "male" && m.genderRaw !== "female").length
    };
  }, [mapMembersToRows, managers]);

  // Gender counts for Monitors
  const monitorGenderCounts = useMemo(() => {
    const rows = mapMembersToRows(monitors);
    return {
      all: rows.length,
      male: rows.filter(m => m.genderRaw === "male").length,
      female: rows.filter(m => m.genderRaw === "female").length,
      other: rows.filter(m => m.genderRaw !== "male" && m.genderRaw !== "female").length
    };
  }, [mapMembersToRows, monitors]);

  // Overall Gender KPI Summary
  const overallGenderStats = useMemo(() => {
    const totalFemale = managerGenderCounts.female + monitorGenderCounts.female;
    const totalMale = managerGenderCounts.male + monitorGenderCounts.male;
    const totalOther = managerGenderCounts.other + monitorGenderCounts.other;
    const totalMembers = managers.length + monitors.length;
    return {
      totalFemale,
      totalMale,
      totalOther,
      totalMembers
    };
  }, [managerGenderCounts, monitorGenderCounts, managers.length, monitors.length]);

  const filteredManagers = useMemo(() => {
    const rows = mapMembersToRows(managers);
    return rows.filter(member => {
      const search = managerSearchTerm.toLowerCase().trim();
      const matchesSearch =
        !search ||
        member.name?.toLowerCase().includes(search) ||
        member.email?.toLowerCase().includes(search) ||
        member.gender?.toLowerCase().includes(search) ||
        member.genderRaw?.toLowerCase().includes(search) ||
        member.status?.toLowerCase().includes(search) ||
        (member.country && member.country.toLowerCase().includes(search));

      const matchesStatus = managerStatusFilter === "all" || member.statusSuffix === managerStatusFilter;

      const matchesGender =
        managerGenderFilter === "all" ||
        (managerGenderFilter === "male" && member.genderRaw === "male") ||
        (managerGenderFilter === "female" && member.genderRaw === "female") ||
        (managerGenderFilter === "other" && member.genderRaw !== "male" && member.genderRaw !== "female");

      return matchesSearch && matchesStatus && matchesGender;
    });
  }, [mapMembersToRows, managers, managerSearchTerm, managerStatusFilter, managerGenderFilter]);

  const filteredMonitors = useMemo(() => {
    const rows = mapMembersToRows(monitors);
    return rows.filter(member => {
      const search = monitorSearchTerm.toLowerCase().trim();
      const matchesSearch =
        !search ||
        member.name?.toLowerCase().includes(search) ||
        member.email?.toLowerCase().includes(search) ||
        member.gender?.toLowerCase().includes(search) ||
        member.genderRaw?.toLowerCase().includes(search) ||
        member.status?.toLowerCase().includes(search) ||
        (member.country && member.country.toLowerCase().includes(search));

      const matchesStatus = monitorStatusFilter === "all" || member.statusSuffix === monitorStatusFilter;

      const matchesGender =
        monitorGenderFilter === "all" ||
        (monitorGenderFilter === "male" && member.genderRaw === "male") ||
        (monitorGenderFilter === "female" && member.genderRaw === "female") ||
        (monitorGenderFilter === "other" && member.genderRaw !== "male" && member.genderRaw !== "female");

      return matchesSearch && matchesStatus && matchesGender;
    });
  }, [mapMembersToRows, monitors, monitorSearchTerm, monitorStatusFilter, monitorGenderFilter]);

  if (!team) {
    return <Loader isLoading />;
  }

  return (
    <>
      <Loader isLoading={isTeamLoading || isFetchingAreas || isProfilesLoading} />
      <Hero
        title="teams.details.name"
        titleValues={{ name: team?.attributes?.name || "" }}
        actions={
          <>
            {userIsManager && (
              <Link to={`${location.pathname}/edit`} className="c-teams-details__edit-btn c-button c-button--primary">
                <FormattedMessage id="teams.details.edit" />
              </Link>
            )}
            {userIsAdmin && (
              <Link to={`${location.pathname}/delete`} className="c-button c-button--secondary-light-text">
                <FormattedMessage id="teams.details.delete" />
              </Link>
            )}
          </>
        }
        backLink={{ name: "teams.details.back", to: "/teams" }}
      />
      <div className="l-content c-teams-details u-padding-bottom-none">
        <div className="row column">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-2">
            {/* Card 1: Team Capacity & Roles */}
            <div className="bg-white p-5 rounded-xl border border-solid border-neutral-500 shadow-sm">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-700 mb-1">
                Team Capacity & Roles
              </p>
              <h4 className="text-2xl font-bold text-neutral-700">
                {overallGenderStats.totalMembers} <span className="text-sm font-normal text-neutral-700">Members</span>
              </h4>
              <p className="text-xs text-neutral-700 mt-1">
                <span className="font-semibold text-neutral-700">{managers.length}</span> Managers •{" "}
                <span className="font-semibold text-neutral-700">{monitors.length}</span> Monitors
              </p>
              <p className="text-xs text-neutral-700 mt-2 pt-2 border-t border-solid border-neutral-500">
                <span className="font-semibold text-neutral-700">Gender Balance:</span>{" "}
                {overallGenderStats.totalFemale > 0 || overallGenderStats.totalMale > 0 ? (
                  <>
                    <span className="font-medium text-neutral-700">{overallGenderStats.totalFemale} Female</span> •{" "}
                    <span className="font-medium text-neutral-700">{overallGenderStats.totalMale} Male</span>
                    {overallGenderStats.totalOther > 0 ? ` • ${overallGenderStats.totalOther} Not specified` : ""}
                  </>
                ) : (
                  <span className="text-neutral-700">{overallGenderStats.totalOther} Not specified</span>
                )}
              </p>
            </div>

            {/* Card 2: Managed Areas */}
            <div className="bg-white p-5 rounded-xl border border-solid border-neutral-500 shadow-sm">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-700 mb-1">
                Coverage & Territory
              </p>
              <h4 className="text-2xl font-bold text-neutral-700">
                {teamAreas.length} <span className="text-sm font-normal text-neutral-700">Areas Linked</span>
              </h4>
              <p className="text-xs text-neutral-700 mt-1">Monitored by Team</p>
            </div>

            {/* Card 3: User Role Status */}
            <div className="bg-white p-5 rounded-xl border border-solid border-neutral-500 shadow-sm">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-700 mb-1">Your Access Role</p>
              <h4 className="text-lg font-bold text-neutral-700">
                {userIsAdmin ? "Team Admin" : userIsManager ? "Team Manager" : "Field Monitor"}
              </h4>
              <p className="text-xs text-neutral-700 mt-1">
                {userIsManager ? "Can designate roles & assign tasks" : "Active field contributor"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="l-content c-teams-details">
        <Article
          className="c-teams-details__heading"
          title="teams.details.managers"
          titleValues={{ num: managers.length }}
          size="small"
        >
          {managers.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 mt-2">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                <input
                  type="text"
                  value={managerSearchTerm}
                  onChange={e => setManagerSearchTerm(e.target.value)}
                  placeholder="Search name, email, gender..."
                  className="w-full pl-8 pr-8 py-2 text-sm text-neutral-700 bg-white border border-solid border-neutral-500 rounded-md focus:outline-none focus:border-primary-500"
                />
                <span className="absolute left-2.5 top-2.5 text-neutral-700 text-xs">🔍</span>
                {managerSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setManagerSearchTerm("")}
                    className="absolute right-2.5 top-2.5 text-xs text-neutral-700 hover:text-black-dark"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Clean Matching Dropdown Filters */}
              <div className="flex flex-wrap items-center gap-2.5">
                {/* Gender Dropdown */}
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border border-solid border-neutral-500 text-sm text-neutral-700">
                  <span className="font-semibold text-neutral-700">Gender:</span>
                  <select
                    value={managerGenderFilter}
                    onChange={e => setManagerGenderFilter(e.target.value as any)}
                    className="bg-white text-sm font-semibold text-neutral-700 focus:outline-none cursor-pointer border-none p-0 pr-1"
                  >
                    <option value="all">All ({managerGenderCounts.all})</option>
                    <option value="female">Female ({managerGenderCounts.female})</option>
                    <option value="male">Male ({managerGenderCounts.male})</option>
                    {managerGenderCounts.other > 0 && (
                      <option value="other">Not specified ({managerGenderCounts.other})</option>
                    )}
                  </select>
                </div>

                {/* Status Dropdown */}
                {userIsManager && (
                  <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border border-solid border-neutral-500 text-sm text-neutral-700">
                    <span className="font-semibold text-neutral-700">Status:</span>
                    <select
                      value={managerStatusFilter}
                      onChange={e => setManagerStatusFilter(e.target.value as any)}
                      className="bg-white text-sm font-semibold text-neutral-700 focus:outline-none cursor-pointer border-none p-0 pr-1"
                    >
                      <option value="all">All ({managers.length})</option>
                      <option value="confirmed">
                        Confirmed ({managers.filter(m => m.status === "confirmed").length})
                      </option>
                      <option value="invited">Invited ({managers.filter(m => m.status === "invited").length})</option>
                    </select>
                  </div>
                )}

                {/* Reset button */}
                {(managerSearchTerm || managerGenderFilter !== "all" || managerStatusFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setManagerSearchTerm("");
                      setManagerGenderFilter("all");
                      setManagerStatusFilter("all");
                    }}
                    className="px-2 py-1 text-sm text-neutral-700 hover:text-red-600 font-medium transition-colors underline cursor-pointer"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
          <DataTable<TTeamDetailDataTable>
            className="u-w-100"
            rows={filteredManagers}
            columnOrder={userIsManager ? columnOrderWithStatus : columnOrder}
            rowActions={userIsManager ? [makeAdmin, makeMonitor, removeMember] : undefined}
          />
        </Article>
      </div>
      <div className="l-content c-teams-details">
        <Article
          className="c-teams-details__heading"
          title="teams.details.monitors"
          titleValues={{ num: monitors.length }}
          size="small"
          actions={
            userIsManager && (
              <Link to={`${location.pathname}/add/monitor`} className="c-button c-button--primary">
                <img className="c-button__inline-icon" src={PlusIcon} alt="" role="presentation" />
                <FormattedMessage id="teams.details.add.monitors" />
              </Link>
            )
          }
        >
          {monitors.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 mt-2">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                <input
                  type="text"
                  value={monitorSearchTerm}
                  onChange={e => setMonitorSearchTerm(e.target.value)}
                  placeholder="Search name, email, gender..."
                  className="w-full pl-8 pr-8 py-2 text-sm text-neutral-700 bg-white border border-solid border-neutral-500 rounded-md focus:outline-none focus:border-primary-500"
                />
                <span className="absolute left-2.5 top-2.5 text-neutral-700 text-xs">🔍</span>
                {monitorSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setMonitorSearchTerm("")}
                    className="absolute right-2.5 top-2.5 text-xs text-neutral-700 hover:text-black-dark"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Clean Matching Dropdown Filters */}
              <div className="flex flex-wrap items-center gap-2.5">
                {/* Gender Dropdown */}
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border border-solid border-neutral-500 text-sm text-neutral-700">
                  <span className="font-semibold text-neutral-700">Gender:</span>
                  <select
                    value={monitorGenderFilter}
                    onChange={e => setMonitorGenderFilter(e.target.value as any)}
                    className="bg-white text-sm font-semibold text-neutral-700 focus:outline-none cursor-pointer border-none p-0 pr-1"
                  >
                    <option value="all">All ({monitorGenderCounts.all})</option>
                    <option value="female">Female ({monitorGenderCounts.female})</option>
                    <option value="male">Male ({monitorGenderCounts.male})</option>
                    {monitorGenderCounts.other > 0 && (
                      <option value="other">Not specified ({monitorGenderCounts.other})</option>
                    )}
                  </select>
                </div>

                {/* Status Dropdown */}
                {userIsManager && (
                  <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border border-solid border-neutral-500 text-sm text-neutral-700">
                    <span className="font-semibold text-neutral-700">Status:</span>
                    <select
                      value={monitorStatusFilter}
                      onChange={e => setMonitorStatusFilter(e.target.value as any)}
                      className="bg-white text-sm font-semibold text-neutral-700 focus:outline-none cursor-pointer border-none p-0 pr-1"
                    >
                      <option value="all">All ({monitors.length})</option>
                      <option value="confirmed">
                        Confirmed ({monitors.filter(m => m.status === "confirmed").length})
                      </option>
                      <option value="invited">Invited ({monitors.filter(m => m.status === "invited").length})</option>
                    </select>
                  </div>
                )}

                {/* Reset button */}
                {(monitorSearchTerm || monitorGenderFilter !== "all" || monitorStatusFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setMonitorSearchTerm("");
                      setMonitorGenderFilter("all");
                      setMonitorStatusFilter("all");
                    }}
                    className="px-2 py-1 text-sm text-neutral-700 hover:text-red-600 font-medium transition-colors underline cursor-pointer"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
          <DataTable<TTeamDetailDataTable>
            className="u-w-100"
            rows={filteredMonitors}
            columnOrder={userIsManager ? columnOrderWithStatus : columnOrder}
            rowActions={userIsManager ? [makeManager, removeMember] : undefined}
          />
        </Article>
      </div>

      <div className="l-content c-teams-details">
        <Article
          className="c-teams-details__heading"
          title="teams.details.areas"
          titleValues={{ num: teamAreas.length }}
          size="small"
        >
          <DataTable<TAreaDataTable>
            className="u-w-100"
            rows={mapAreasToRows(teamAreas)}
            columnOrder={areaColumnOrder}
            rowActions={[investigateArea, removeArea]}
          />
        </Article>
      </div>

      <AddTeamMember isOpen={isAddingTeamMember} />
      <EditTeamMember isOpen={isEditingTeamMember} />
      <RemoveTeamMember isOpen={isRemovingTeamMember} />
      <EditTeam isOpen={isEditingTeam} currentName={team?.attributes?.name || ""} />
      <DeleteTeam isOpen={isDeletingTeam} teamId={teamId} />
      <RemoveAreaFromTeam isOpen={isDeletingTeamArea} />
    </>
  );
};

export default TeamDetail;
